import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiCreditCard } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { getDerasar, getAllLedgerAccounts, updateOnlinePaymentBank } from '../../services/api';
import { getApiErrorMessage } from '../../services/apiError';
import styles from './OnlinePaymentBank.module.css';

const OnlinePaymentBank = () => {
  const { activeDerasarId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [derasar, setDerasar] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [ledgerId, setLedgerId] = useState('');

  const load = useCallback(() => {
    if (!activeDerasarId) return;
    setLoading(true);
    Promise.all([
      getDerasar(activeDerasarId),
      getAllLedgerAccounts(activeDerasarId).catch(() => ({ data: [] })),
    ])
      .then(([dRes, accRes]) => {
        const d = dRes.data;
        setDerasar(d);
        setLedgerId(d.onlinePaymentDebitLedgerId || '');
        setBankAccounts((accRes.data || []).filter((a) => a.accountType === 'bank' && a.isActive));
      })
      .catch((err) => toast.error(getApiErrorMessage(err, 'Failed to load settings')))
      .finally(() => setLoading(false));
  }, [activeDerasarId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await updateOnlinePaymentBank(activeDerasarId, { onlinePaymentDebitLedgerId: ledgerId || null });
      toast.success('Settlement bank account updated');
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.page}><p className={styles.muted}>Loading…</p></div>;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2><FiCreditCard /> Online Payments</h2>
      </div>

      {!derasar?.onlinePaymentEnabled ? (
        <div className={styles.notice}>
          Online payment is not enabled for your Derasar. Please contact the platform administrator.
        </div>
      ) : (
        <div className={styles.card}>
          <div className={styles.statusRow}>
            <span className={styles.badgeOn}>Enabled</span>
            {derasar.razorpayKeyId && (
              <span className={styles.keyId}>Key ID: {derasar.razorpayKeyId}</span>
            )}
          </div>

          <div className={styles.field}>
            <label>Settlement Bank Account (Debit Account for Receipts)</label>
            <select value={ledgerId} onChange={(e) => setLedgerId(e.target.value)}>
              <option value="">— Select bank account —</option>
              {bankAccounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name} [{a.accountCode}]</option>
              ))}
            </select>
            <p className={styles.muted}>This account is debited when a member makes an online payment.</p>
          </div>

          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default OnlinePaymentBank;
